// @flow
import { isEmpty } from 'lodash';
import * as React from 'react';
import { withLeaflet } from 'react-leaflet';
import { FormattedMessage } from 'react-intl';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import Creator from 'esm-web-pro/components/maps/Creator';
import MapObjectForm from 'esm-web-pro/containers/map/forms/MapObjectForm';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';

type Props = {
    id: string,
    context: { uuid: string, identity: string },
    leaflet: LeafletContext,
    createObject: (resourceName: string, map: string, data: Object) => Promise<Object>,
    drawingFeatureGroup: { current: null | (React.Node & { leafletElement: Object })},
    onClose: Function,
    editor: BaseEditor,
}

type State = {
    drawedLayer: Object,
    isDrawing: boolean,
    canDraw: boolean,
}


@withLeaflet
@connectAPI((apiClient: ApiClient) => ({
    createObject: {
        send: (resourceName, map, data) => apiClient.resources[resourceName].create(map, data),
    },
}))
class CoverageCreator extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { drawedLayer: null, isDrawing: false, canDraw: false };
        this.formikRef = React.createRef();
    }

    componentDidMount() {
        const { leaflet: { map }, drawingFeatureGroup, editor } = this.props;
        const { drawedLayer } = this.state;

        editor.setDrawing(true);

        if (map && !drawedLayer) {
            map.on('pm:drawstart', () => {
                this.setState({ isDrawing: true });
            });
            map.on('pm:create', (e) => {
                this.setState({
                    drawedLayer: e.layer,
                });
                if (drawingFeatureGroup.current) {
                    e.layer.addTo(drawingFeatureGroup.current.leafletElement);
                }

                e.layer.pm.enable({
                    allowSelfIntersection: false,
                });
            });
        }
        map.pm.enableDraw('Polygon', { snappable: true });
    }

    componentWillUnmount() {
        const { drawedLayer } = this.state;

        const { leaflet: { map }, editor } = this.props;
        if (drawedLayer) {
            drawedLayer.remove();
        }

        if (map) {
            map.pm.Draw.Polygon.disable();
            map.off('pm:drawstart');
            map.off('pm:create');
        }
        editor.setDrawing(false);
    }

    handleFormChange = () => {
        if (this.formikRef.current) {
            this.formikRef.current.getFormikBag().validateForm().then(() => {
                this.setState({ canDraw: isEmpty(this.formikRef.current.getFormikBag().errors) });
            });
        } else {
            this.setState({ canDraw: false });
        }
    };

    handleSave = () => {
        const { drawedLayer } = this.state;
        const {
            createObject, context, onClose, editor,
        } = this.props;
        const drawingData = { polygon: drawedLayer.toGeoJSON().geometry };
        const formData = this.formikRef.current.getFormikBag().values;
        this.formikRef.current.setTouched(Object.keys(formData).reduce((touched, k) => {
            touched[k] = true;
            return touched;
        }, {}));
        createObject('coverage.geo', context.uuid, { ...formData, ...drawingData }).then((response) => {
            if (response.status === HTTP_400_BAD_REQUEST) {
                this.setState({
                    isDrawing: true,
                });
                this.formikRef.current.setErrors(response.data);
            } else {
                this.setState({ drawedLayer: null });
                editor.setDrawing(false);
                drawedLayer.remove();
                editor._loadFeatures(true);
                onClose(true);
            }
        });
    };

    handleClose = () => {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
    };

    render() {
        const {
            context, id,
        } = this.props;
        const { isDrawing, drawedLayer, canDraw } = this.state;

        return (
            <Creator
                id={id}
                title={(
                    <FormattedMessage
                        id="coveragecreator-title"
                        defaultMessage="Create Coverage"
                    />
                )}
                onClose={this.handleClose}
                onSave={this.handleSave}
                isDrawing={isDrawing}
                canSave={!!drawedLayer}
                formikRef={this.formikRef}
            >
                <MapObjectForm
                    key="coverage"
                    resourceName="coverage.geo"
                    resourceArguments={[context.uuid]}
                    excludeFields={['polygon']}
                    forwardRef={this.formikRef}
                    onChange={this.handleFormChange}
                    onInitialize={this.handleFormChange}
                />
            </Creator>
        );
    }
}

export default CoverageCreator;
